import { createContext, useContext, useEffect, useMemo, useReducer, type PropsWithChildren } from "react";
import { getDefaultRemovedIngredientIds } from "../lib/menuDefaults";
import { useMenu } from "./MenuContext";
import { useCustomer } from "./CustomerContext";
import type { MenuAddOnOption, MenuDish, MenuIngredientOption } from "../types/menu";

interface DishCustomizationSelection {
  removedIngredientIds?: string[];
  addOnIds?: string[];
}

interface CartSnapshotLine {
  dishId: string;
  qty: number;
  removedIngredientIds?: string[];
  addOnIds?: string[];
}

interface CartLineState {
  lineId: string;
  dishId: string;
  qty: number;
  removedIngredientIds: string[];
  addOnIds: string[];
}

type CartState = CartLineState[];

const CART_STORAGE_KEY = "chaykhana-izumrud-cart-v1";

type CartAction =
  | {
      type: "add";
      line: Omit<CartLineState, "qty">;
    }
  | { type: "increment"; lineId: string }
  | { type: "decrement"; lineId: string }
  | { type: "replace"; lines: CartLineState[] }
  | { type: "clear" };

interface CartEntry {
  id: string;
  lineId: string;
  qty: number;
  dish: MenuDish;
  removedIngredients: MenuIngredientOption[];
  addOns: MenuAddOnOption[];
  unitPrice: number;
  lineTotal: number;
}

interface CartContextValue {
  items: Record<string, number>;
  itemCount: number;
  subtotal: number;
  delivery: number;
  total: number;
  entries: CartEntry[];
  addItem: (id: string, customization?: DishCustomizationSelection) => void;
  replaceWithItems: (items: CartSnapshotLine[]) => void;
  increment: (target: string) => void;
  decrement: (target: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function normalizeIds(ids?: string[]) {
  return Array.from(new Set(ids ?? [])).sort();
}

function createLineId(dishId: string, customization?: DishCustomizationSelection) {
  const removedIngredientIds = normalizeIds(customization?.removedIngredientIds);
  const addOnIds = normalizeIds(customization?.addOnIds);

  return `${dishId}::rm=${removedIngredientIds.join(",")}::ad=${addOnIds.join(",")}`;
}

function createLineState(dishId: string, customization?: DishCustomizationSelection) {
  return {
    lineId: createLineId(dishId, customization),
    dishId,
    removedIngredientIds: normalizeIds(customization?.removedIngredientIds),
    addOnIds: normalizeIds(customization?.addOnIds)
  };
}

function resolveLineId(state: CartState, target: string) {
  if (state.some((line) => line.lineId === target)) {
    return target;
  }

  return createLineId(target);
}

function isStoredLine(value: unknown): value is CartLineState {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<CartLineState>;

  return (
    typeof candidate.lineId === "string" &&
    typeof candidate.dishId === "string" &&
    typeof candidate.qty === "number" &&
    Array.isArray(candidate.removedIngredientIds) &&
    Array.isArray(candidate.addOnIds)
  );
}

function restoreLineState(line: CartLineState): CartLineState {
  const customization = {
    removedIngredientIds: normalizeIds(line.removedIngredientIds),
    addOnIds: normalizeIds(line.addOnIds)
  };

  return {
    dishId: line.dishId,
    lineId: createLineId(line.dishId, customization),
    qty: Math.max(1, Math.floor(line.qty)),
    removedIngredientIds: customization.removedIngredientIds,
    addOnIds: customization.addOnIds
  };
}

function readInitialCartState(): CartState {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isStoredLine).map(restoreLineState);
  } catch {
    return [];
  }
}

function cartReducer(state: CartState, action: CartAction) {
  if (action.type === "clear") {
    return [];
  }

  if (action.type === "replace") {
    return action.lines;
  }

  if (action.type === "add") {
    const existing = state.find((line) => line.lineId === action.line.lineId);

    if (existing) {
      return state.map((line) =>
        line.lineId === action.line.lineId ? { ...line, qty: line.qty + 1 } : line
      );
    }

    return [...state, { ...action.line, qty: 1 }];
  }

  return state
    .map((line) => {
      if (line.lineId !== action.lineId) {
        return line;
      }

      const nextQty = action.type === "decrement" ? line.qty - 1 : line.qty + 1;
      return { ...line, qty: nextQty };
    })
    .filter((line) => line.qty > 0);
}

function getDelivery(subtotal: number) {
  return subtotal === 0 || subtotal >= 2800 ? 0 : 180;
}

export function CartProvider({ children }: PropsWithChildren) {
  const { getDishById } = useMenu();
  const { preferences } = useCustomer();
  const [lines, dispatch] = useReducer(cartReducer, undefined, readInitialCartState);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  const value = useMemo<CartContextValue>(() => {
    const entries = lines
      .map((line) => {
        const dish = getDishById(line.dishId);

        if (!dish) {
          return null;
        }

        const removedIngredients =
          dish.customization?.removableIngredients?.filter((option) =>
            line.removedIngredientIds.includes(option.id)
          ) ?? [];

        const addOns =
          dish.customization?.addOns?.filter((option) => line.addOnIds.includes(option.id)) ?? [];

        const unitPrice = dish.price + addOns.reduce((sum, option) => sum + option.price, 0);

        return {
          id: line.dishId,
          lineId: line.lineId,
          qty: line.qty,
          dish,
          removedIngredients,
          addOns,
          unitPrice,
          lineTotal: unitPrice * line.qty
        };
      })
      .filter((entry): entry is CartEntry => Boolean(entry));

    const subtotal = entries.reduce((sum, entry) => sum + entry.lineTotal, 0);
    const delivery = getDelivery(subtotal);
    const itemCount = entries.reduce((sum, entry) => sum + entry.qty, 0);
    const items = entries.reduce<Record<string, number>>((accumulator, entry) => {
      accumulator[entry.id] = (accumulator[entry.id] ?? 0) + entry.qty;
      return accumulator;
    }, {});

    return {
      items,
      itemCount,
      subtotal,
      delivery,
      total: subtotal + delivery,
      entries,
      addItem: (id, customization) => {
        const dish = getDishById(id);
        const nextCustomization =
          !customization && dish
            ? {
                removedIngredientIds: getDefaultRemovedIngredientIds(dish, preferences)
              }
            : customization;

        dispatch({
          type: "add",
          line: createLineState(id, nextCustomization)
        });
      },
      replaceWithItems: (items) => {
        const nextLines = items.reduce<CartState>((accumulator, item) => {
          const qty = Math.max(1, Math.floor(item.qty || 1));
          const line = createLineState(item.dishId, {
            removedIngredientIds: item.removedIngredientIds,
            addOnIds: item.addOnIds
          });
          const existing = accumulator.find((entry) => entry.lineId === line.lineId);

          if (existing) {
            existing.qty += qty;
            return accumulator;
          }

          accumulator.push({
            ...line,
            qty
          });
          return accumulator;
        }, []);

        dispatch({
          type: "replace",
          lines: nextLines
        });
      },
      increment: (target) =>
        dispatch({
          type: "increment",
          lineId: resolveLineId(lines, target)
        }),
      decrement: (target) =>
        dispatch({
          type: "decrement",
          lineId: resolveLineId(lines, target)
        }),
      clear: () => dispatch({ type: "clear" })
    };
  }, [getDishById, lines, preferences]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }

  return context;
}
